import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';

import { useManageVisitReportModalStore } from './MangeVisitReportModalStore';

import { CreateVisitReportFormValues, createVisitReportSchema } from 'api/visitReports/type';
import { MLInput } from 'components/Input/Input';
import useCreateVisitReport from 'hooks/VisitReport/createVisitReport/useCreateVisitReport';
import useGetVisitsToObject from 'hooks/VisitReport/createVisitReport/useGetVitistsToObject';
import { MLSelect } from 'components/Select/Select';

interface CreateVisitReportModalProps {
  refetchVisitReports: () => void;
  objectSlug: string;
}

const CreateVisitReportModal: FC<CreateVisitReportModalProps> = ({ refetchVisitReports, objectSlug }) => {
  const { createVisitReport } = useCreateVisitReport();
  const { slug, isOpen, onManageVisitReportModalClose } = useManageVisitReportModalStore();
  const { t } = useTranslation();
  const { visitToObjectOptions } = useGetVisitsToObject(objectSlug);

  const { handleSubmit, control } = useForm<CreateVisitReportFormValues>({
    resolver: zodResolver(createVisitReportSchema),
    defaultValues: {
      start_date: '',
      end_date: '',
      visit_pk: 0,
    },
  });

  const onCreateVisitReport = (data: CreateVisitReportFormValues) => {
    createVisitReport(
      { createVisitReportData: data, slug: slug || '' },
      {
        onSuccess: () => {
          enqueueSnackbar('Visit report was created successfully', { hideIconVariant: true, variant: 'success' });
          refetchVisitReports();
          onManageVisitReportModalClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onManageVisitReportModalClose} size="md">
      <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={onManageVisitReportModalClose}>
        Create visit report
      </ModalHeader>
      <ModalBody>
        <div className="row m-2">
          <div className="col-md-6">
            <Controller
              control={control}
              name="start_date"
              render={({ field, fieldState: { error } }) => (
                <MLInput type="date" label="Start date" error={error?.message} required {...field} />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={control}
              name="end_date"
              render={({ field, fieldState: { error } }) => (
                <MLInput type="date" label="End date" error={error?.message} required {...field} />
              )}
            />
          </div>
        </div>
        <div className="row m-2">
          <div className="col-xxl-12">
            <Controller
              control={control}
              name="visit_pk"
              render={({ field, formState: { errors } }) => (
                <MLSelect
                  {...field}
                  label="Choose visit"
                  error={errors?.visit_pk?.message}
                  value={+field.value} // Convert to number here
                  required
                  onChange={selectedOption => {
                    field.onChange(selectedOption);
                  }} // Convert to number here as well
                  options={
                    !visitToObjectOptions || visitToObjectOptions.length === 0
                      ? [{ value: 0, label: 'No visits to object' }]
                      : [
                          { value: 0, label: 'Select visit date' },
                          ...visitToObjectOptions.map(visit => ({
                            value: visit.id, // `visit.id` is already a number
                            label: visit.visit_date,
                          })),
                        ]
                  }
                />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onManageVisitReportModalClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>{' '}
        <Button className="btn btn-ghost-success" onClick={handleSubmit(onCreateVisitReport)}>
          Create report
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateVisitReportModal;
